// src/components/DashboardItem.js
import React, { useState } from 'react';
import { ListItem, ListItemIcon, ListItemText, Tooltip, Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import dashboardLogo from '../assets/grid-icon.svg';
import { useNavigate } from 'react-router-dom';
import { useTheme, styled } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import { ReactComponent as EditIcon } from '../assets/edit.svg';
import CreateDashboardModal from './CreateDashboardModal';
import DeleteDashboardModal from './DeleteDashboardModal';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDashboards } from '../store/dashboardSlice';

const StyledMenuItem = styled(MenuItem)(({ theme, isDelete }) => ({
  display: 'flex',
  gap: '8px',
  padding: '10px 16px',
  borderRadius: '12px',
  margin: '2px 0',
  transition: '0.2s ease',
  background: 'transparent',
  '&:hover': {
    background: isDelete 
      ? 'rgba(211, 47, 47, 0.1)'
      : 'rgba(18, 157, 232, 0.1)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
  },
  '& .MuiSvgIcon-root': {
    color: isDelete ? '#d32f2f' : '#000000',
  },
  '& .MuiTypography-root': {
    color: isDelete ? '#d32f2f' : '#000000',
    fontWeight: '400',
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1.2px solid rgba(200, 200, 200, 0.5)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
    padding: '0px 8px',
    width: '115px',
  },
  '& .MuiList-root': {
    padding: '4px 0px',
  },
}));

function DashboardItem({ dashboard, isOpen, isActive, navigationDisabled = false }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Get user ID from auth state
  const authState = useSelector((state) => state.auth);
  
  // Callback to refresh dashboards after deletion
  const handleDashboardDeleted = () => {
    const currentUserId = authState.user?.user?.id;
    if (currentUserId) {
      dispatch(fetchDashboards(currentUserId));
    }
  };

  const handleClick = () => {
    if (navigationDisabled) return; // Block navigation during streaming
    navigate(`/dashboard/${dashboard.dashboard_id}`);
  };

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleRenameClick = () => {
    setShowRenameModal(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    handleMenuClose();
  };

  return (
    <>
      <ListItem 
        button 
        onClick={handleClick} 
        selected={isActive}
        sx={{
          cursor: 'pointer',
          backgroundColor: isActive ? 'rgba(0, 123, 255, 0.1)' : 'transparent',
          borderRadius: '8px',
          mx: 1,
          width: 'calc(100% - 16px)',
          padding: '4px 8px',
          height: '36px',
          boxSizing: 'border-box',
          '&:hover': {
            backgroundColor: isActive ? 'rgba(0, 123, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
            '& .more-options': {
              opacity: 1,
            },
          },
          '&.Mui-selected': {
            backgroundColor: '#D3DCF0',
            '&:hover': {
              backgroundColor: '#D3DCF0',
            }
          }
        }}
      >
        <ListItemIcon sx={{ 
          color: 'inherit', 
          minWidth: 0,
          justifyContent: 'center',
          mr: isOpen ? 1.5 : 0,
        }}>
          {!isOpen ? (
            <Tooltip title={dashboard.title} placement="right">
               <Box component="img" src={dashboardLogo} alt="Dashboard Logo" sx={{ width: 24, height: 24 }} />
            </Tooltip>
          ) : (
            <Box component="img" src={dashboardLogo} alt="Dashboard Logo" sx={{ width: 24, height: 24, ml: '-1.5px' }} />
          )}
        </ListItemIcon>
        
        {isOpen && (
          <>
            <ListItemText 
              primary={dashboard.title} 
              primaryTypographyProps={{
                 noWrap: true,
                 sx: { 
                   fontSize: '13px', 
                   marginLeft: '-5px',
                   fontFamily: 'InterTight, sans-serif',
                   fontWeight: isActive ? 'medium' : 'normal',
                   color: theme.palette.sidebar.text
                }
             }}
            />
            <IconButton
              className="more-options"
              edge="end"
              sx={{
                opacity: 0,
                transition: 'opacity 0.2s',
                zIndex: 1,
                borderRadius: '4px',
                padding: '4px',
                ml: 'auto',
                mr: 0,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
              onClick={handleMenuOpen}
            >
              <MoreVertIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          </>
        )}
      </ListItem>

      {/* Menu for dashboard options */}
      <StyledMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{ ml: '17px', mt: '-8px' }}
      >
        <StyledMenuItem onClick={handleRenameClick}>
          <EditIcon style={{ width: 16, height: 16 }} />
          <Typography sx={{ ml: '1.5px', fontSize: '14px' }}>Rename</Typography>
        </StyledMenuItem>
        <StyledMenuItem onClick={handleDeleteClick} isDelete>
          <DeleteIcon style={{ width: 18, height: 18 }} />
          <Typography sx={{ fontSize: '14px' }}>Delete</Typography>
        </StyledMenuItem>
      </StyledMenu>

      {/* Rename Modal */}
      <CreateDashboardModal
        open={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        dashboardToEdit={dashboard}
      />

      {/* Delete Modal */}
      <DeleteDashboardModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        dashboard={dashboard}
        onSuccess={handleDashboardDeleted}
      />
    </>
  );
}

export default React.memo(DashboardItem);
